import { DateTimeFormat, getDateFormat, type SystemTextMessagesDto } from "@ews/react-data";
import { useL10n } from '@ews/react-localization-context';
import { IonButton, IonHeader, IonItem, IonList, IonModal, IonText, IonTitle, IonToolbar } from '@ionic/react';
import { useEffect, useRef, useState } from 'react';
import { Locales, localeTranslationKeys } from '../../../../../setup/locale';
import { useCreateSystemTextMessages, useModifySystemTextMessages } from '../../../../ReactData/system';
import SettingsCheckbox from '../../../../Settings/SettingsComponents/SettingsCeckbox/SettingsCeckbox';
import SettingsSelect from '../../../../Settings/SettingsComponents/SettingsSelect/SettingsSelect';
import SettingsSelectOption from '../../../../Settings/SettingsComponents/SettingsSelectOption/SettingsSelectOption';
import SettingsTextInput from '../../../../Settings/SettingsComponents/SettingsTextInput/SettingsTextInput';
import style from "./Modal.module.scss";
import type { ModalProps } from './types';


const Modal: React.FC<ModalProps> = ({ systemId, isOpen, type, data, onDismiss, onUpdate, title }) =>
{
    const languages = Locales.available;

    const { translate: t, getLocale } = useL10n();

    const defaultLocale = getLocale() || "de";
    const standard: SystemTextMessagesDto = {
        telephoneNumber: "",
        alarm: false,
        preAlarm: false,
        locale: defaultLocale,
        dateTimeFormat: DateTimeFormat.DE,
    };

    const modal = useRef<HTMLIonModalElement>(null);

    const [_data, setData] = useState<SystemTextMessagesDto>(standard);


    const disableSaveButton = !(_data.telephoneNumber && _data.locale && _data.dateTimeFormat);


    const [error, setError] = useState("");

    const updateTextMesssage = useModifySystemTextMessages();

    const createTextMessage = useCreateSystemTextMessages();

    async function edit()
    {
        if (systemId && data?.id) {
            await updateTextMesssage(systemId, _data, data.id);
        };
        if (onUpdate) onUpdate();
        close();
    }
    async function create()
    {
        if (systemId) {
            await createTextMessage(systemId, _data);
        };
        if (onUpdate) onUpdate();
        close();
    }

    function onWillDismiss()
    {
        onDismiss();
    }

    function close()
    {
        setData(standard);
        onDismiss();
        modal.current?.dismiss();
    }

    useEffect(() =>
    {
        if (type === "edit" && data) {
            setData({ ...data });
        } else {
            setData(standard);
        }

    }, [data, type]);

    return <>
        <IonModal
            ref={modal}
            className={style.modal}
            isOpen={isOpen}
            onWillDismiss={onWillDismiss}
        >
            <IonHeader>
                <IonToolbar>
                    <div
                        className={style.header}
                    >
                        <IonTitle>{`${t(title)}`}</IonTitle>
                    </div>
                </IonToolbar>
            </IonHeader>
            <IonList
                className="ion-padding"
                color="primary">
                <SettingsTextInput
                    text={"COM_FACPMANAGEMENT_LOGS_SMS_PHONE_NUMBER"}
                    inputType='tel'
                    value={_data?.telephoneNumber || ""}
                    onChange={(telephoneNumber) =>
                    {
                        setError(!telephoneNumber || !telephoneNumber.length ? t("[REACT_PLEASE_ENTER_A_PHONE_NUMBER]") : '');
                        setData({ ..._data, telephoneNumber });
                    }}
                />
                <SettingsTextInput
                    text={"COM_ADMIN_USER_HEADING_NAME"}
                    inputType='text'
                    value={_data?.label || ""}
                    onChange={(label) =>
                    {
                        setError('');
                        setData({ ..._data, label });
                    }}
                />
                <SettingsSelect text={'[SID_MNU_VLI_LANGUAGE]'}
                    value={_data?.locale || getLocale()}
                    onSelect={(locale) =>
                    {
                        setData({ ..._data, locale });
                    }}
                >
                    {languages.map((language, index) =>
                    {
                        const text = t(localeTranslationKeys[language]);
                        return <SettingsSelectOption
                            key={index}
                            text={text}
                            value={language}
                        />;
                    })}
                </SettingsSelect>

                <SettingsSelect
                    text={"COM_FACPMANAGEMENT_CUSTOMER_FIELD_DATETIME_LABEL"}
                    value={_data?.dateTimeFormat || ""}
                    onSelect={(dateTimeFormat) => setData({ ..._data, dateTimeFormat })}
                >
                    {Object.keys(DateTimeFormat).map((time, index) =>
                    {
                        const key = DateTimeFormat[time as keyof typeof DateTimeFormat];
                        return <SettingsSelectOption key={index} text={getDateFormat(key, new Date())} value={key} />;
                    })}
                </SettingsSelect>

                <div className={style.checkboxContainer}>
                    <div className={style.checkboxItem}>

                        <SettingsCheckbox
                            checked={_data.alarm}
                            text={"TD_ALARME"}
                            onChange={(alarm) =>
                            {
                                setData({ ..._data, alarm });
                            }} />
                    </div>
                    <div className={style.checkboxItem}>

                        <SettingsCheckbox
                            checked={_data.preAlarm}
                            text={"[SID_EV_FIRE_PREALARM]"}
                            onChange={(preAlarm) =>
                            {
                                setData({ ..._data, preAlarm });
                            }} />
                    </div>
                </div>

                <IonItem lines={error.length ? "full" : "none"}>
                    <div className={style.error}>
                        <IonText color={"danger"}>{error}</IonText>
                    </div>
                </IonItem>
            </IonList>
            <div className={`${style.buttonContainer} ${style.marginBottom}`}>
                <IonButton
                    slot="start"
                    color={"danger"}
                    onClick={() => close()}>
                    {t("COM_BANNERS_CANCEL")}
                </IonButton>
                <IonButton
                    slot="end"
                    color={"success"}
                    disabled={type === "new" ? disableSaveButton : false}
                    onClick={() =>
                    {
                        if (type === "edit") {
                            edit();
                        } else {
                            create();
                        }
                    }}>
                    {t("COM_FACPMANAGEMENT_USER_SAVE")}
                </IonButton>
            </div>
        </IonModal>
    </>;

};


export default Modal;
